import BoldExtension, { BoldOptions } from "@tiptap/extension-bold";
import BulletListExtension from "@tiptap/extension-bullet-list";
import DocumentExtension from "@tiptap/extension-document";
import HardBreakExtension from "@tiptap/extension-hard-break";
import HistoryExtension, { HistoryOptions } from "@tiptap/extension-history";
import ItalicExtension from "@tiptap/extension-italic";
import LinkExtension from "@tiptap/extension-link";
import ListItemExtension from "@tiptap/extension-list-item";
import OrderedListExtension from "@tiptap/extension-ordered-list";
import ParagraphExtension from "@tiptap/extension-paragraph";
import PlaceholderExtension, {
  PlaceholderOptions,
} from "@tiptap/extension-placeholder";
import TextExtension from "@tiptap/extension-text";
import Heading from "@tiptap/extension-heading";
import Table from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import Image from "@tiptap/extension-image";
import ResizeImage from "tiptap-extension-resize-image";
import Code from "@tiptap/extension-code";
import CodeBlock from "@tiptap/extension-code-block";
import { useMemo } from "react";
import useBrandColor from "@src/features/settings/hooks/useBrandColor";
import { SuggestionOptions } from "@tiptap/suggestion";
import { Mention } from "@tiptap/extension-mention";
import { Node, Mark, Extension } from "@tiptap/core";
import HorizontalRuleExtension from "@tiptap/extension-horizontal-rule";
import "katex/dist/katex.min.css";
import {
  Mathematics,
  MathematicsOptions,
} from "@tiptap-pro/extension-mathematics";
import Gapcursor from "@tiptap/extension-gapcursor";

type Params = {
  placeholder: PlaceholderOptions["placeholder"];
  // When true, show placeholder on every empty node (not just the focused
  // one). Use together with a function-based `placeholder` if you want
  // per-block placeholder text (e.g. a title placeholder on the first
  // block and no placeholder on body blocks).
  showPlaceholderOnEveryEmptyNode?: boolean;
  enableMentions: boolean;
  mentionsConfig?: Omit<SuggestionOptions, "editor">;
  enableMathematics?: boolean;
  enableGapcursor?: boolean;
  enableImages?: boolean;
  enableImageResize?: boolean;
  disableTableHtml?: boolean;
};

type RichTextEditorExtensions = (
  | Node
  | Mark<BoldOptions>
  | Extension<HistoryOptions>
  | Extension<PlaceholderOptions>
  | Extension<MathematicsOptions>
)[];

export const getLinkExtensionHTMLStyles = (brandColor: string): string =>
  `color: ${brandColor}; text-decoration: underline; word-break: break-all;`;

const useRichTextEditorExtensions = (
  args: Params,
): RichTextEditorExtensions => {
  const {
    enableMentions,
    placeholder,
    showPlaceholderOnEveryEmptyNode = false,
    mentionsConfig,
    enableMathematics = false,
    enableGapcursor = false,
    enableImages = true,
    enableImageResize = false,
    disableTableHtml = false,
  } = args;
  const brandColor = useBrandColor();

  return useMemo(() => {
    const extensions = [
      TextExtension,
      ParagraphExtension,
      DocumentExtension,
      BoldExtension,
      ItalicExtension,
      Code,
      CodeBlock,
      HardBreakExtension,
      HorizontalRuleExtension,
      ...(enableImages
        ? [
            (enableImageResize ? ResizeImage : Image).configure({
              inline: true,
            }),
          ]
        : []),
      ...(enableMathematics
        ? [
            Mathematics.configure({
              // Customize regex to only match LaTeX with \( \) and \[ \] delimiters
              // This prevents $ signs from being treated as math delimiters
              regex: /\\\((.+?)\\\)|\\\[(.+?)\\\]/gs,
            }),
          ]
        : []),
      Heading.configure({
        levels: [1, 2, 3, 4],
      }),
      ...(disableTableHtml
        ? []
        : [
            Table.configure({
              HTMLAttributes: {
                class:
                  "border-2 border-gray-5-alpha border-collapse rounded-xl w-full",
              },
            }),
            TableRow.configure(),
            TableHeader.configure(),
            TableCell.configure({
              HTMLAttributes: {
                class:
                  "border-2 border-gray-5-alpha break-words min-w-[60px] min-h-[60px] p-1 text-sm leading-[18px]",
              },
            }),
          ]),
      OrderedListExtension.configure({
        HTMLAttributes: {
          class: "list-decimal",
        },
      }),
      ListItemExtension.configure({
        HTMLAttributes: {
          class: undefined,
        },
      }),
      BulletListExtension.configure({
        HTMLAttributes: {
          class: "list-disc",
        },
      }),
      HistoryExtension,
      LinkExtension.extend({ inclusive: true }).configure({
        openOnClick: false,
        HTMLAttributes: {
          style: getLinkExtensionHTMLStyles(brandColor),
        },
      }),
      PlaceholderExtension.configure({
        placeholder,
        showOnlyCurrent: !showPlaceholderOnEveryEmptyNode,
      }),
      ListKeyboardShortcuts,
    ];
    if (enableMentions) {
      extensions.push(
        Mention.configure({
          HTMLAttributes: {
            class: "mention",
          },
          suggestion: mentionsConfig,
        }),
      );
    }
    if (enableGapcursor) {
      extensions.push(Gapcursor);
    }
    return extensions;
  }, [
    enableMentions,
    mentionsConfig,
    placeholder,
    showPlaceholderOnEveryEmptyNode,
    enableMathematics,
    enableGapcursor,
    enableImages,
    enableImageResize,
    disableTableHtml,
    brandColor,
  ]);
};

const ListKeyboardShortcuts = Extension.create({
  name: "listKeyboardShortcuts",

  addKeyboardShortcuts() {
    return {
      Tab: () => {
        return this.editor.commands.sinkListItem("listItem");
      },
      "Shift-Tab": () => {
        return this.editor.commands.liftListItem("listItem");
      },
    };
  },
});

export default useRichTextEditorExtensions;
